import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import ReviewCard from '@/components/base/ReviewCard';
import { articles, categories } from '@/mocks/articles';
import { reviews } from '@/mocks/reviews';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const filteredCategoryArticles = articles.filter((a) => a.categorySlug === slug);
  const categoryArticles = filteredCategoryArticles.length ? filteredCategoryArticles : articles;

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main>
        {/* Breadcrumb */}
        <div className="bg-background-50 pt-24 pb-4">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{category.name}</span>
            </nav>
          </div>
        </div>

        {/* Category Hero */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <p className="text-sm text-foreground-400 font-medium tracking-wide mb-2">
                  分类浏览
                </p>
                <h1 className="text-2xl md:text-4xl font-heading font-black text-foreground-900">
                  {category.name}
                  <span className="italic text-primary-500 font-heading">专区</span>
                </h1>
              </div>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed lg:max-w-md">
                {category.description}
              </p>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article, idx) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    variant={idx === 1 ? 'featured' : 'default'}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-400 text-lg">该分类暂无文章</p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-primary-500 hover:text-primary-600 font-medium"
                >
                  返回首页查看更多内容
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Reviews */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-left mb-12">
              <span className="inline-block px-4 py-1.5 bg-secondary-100 text-secondary-800 text-xs font-semibold rounded-full mb-4 whitespace-nowrap">
                瓜友热评
              </span>
              <h2 className="text-3xl md:text-5xl font-heading font-black text-foreground-900 leading-tight">
                吃瓜群众们
                <br />
                都在说什么
              </h2>
            </div>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
              {reviews.slice(0, 6).map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 52吃瓜网`,
            description: category.description,
            url: `http://rdstudio.cn/category/${category.slug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'http://rdstudio.cn/' },
                { '@type': 'ListItem', position: 2, name: category.name, item: `http://rdstudio.cn/category/${category.slug}` },
              ],
            },
          }),
        }}
      />
    </>
  );
}